/**
 * Template-swap utility.
 *
 * PPTX Board Packs are generated client-side with pptxgenjs. When a paying
 * user has uploaded their company PPTX template, we merge the template's
 * *theme* (colour scheme + font faces) into the generated deck — that means
 * every slide instantly picks up the customer's brand colours and typography
 * without re-building the deck from scratch.
 *
 * The two zips share the same PPTX structure (Office Open XML), so this is
 * a scoped surgical merge, not a full pptxgenjs rewrite. Files we copy:
 *   ppt/theme/theme1.xml            — colour scheme + font scheme
 *   ppt/slideMasters/slideMaster1.xml — master background/placeholders (best-effort)
 *   ppt/media/imageX.png            — media referenced by the master (if any)
 *
 * Fails safely: if the template can't be parsed, returns the original pack
 * unchanged so the export never breaks.
 */
import JSZip from "jszip";
import { api } from "./api";

const b64ToU8 = (b64) => {
  const bin = atob(b64);
  const arr = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i++) arr[i] = bin.charCodeAt(i);
  return arr;
};

/**
 * Merge the user's PPTX template into the freshly generated Board Pack.
 *
 * @param {Blob} packBlob     — pptxgenjs output blob (from ppt.write("blob"))
 * @returns {Promise<Blob>}   — merged pack blob (falls back to original on failure)
 */
export async function applyCustomPptxTemplate(packBlob) {
  try {
    const dl = await api.templateDownload("pptx");           // { content_b64 }
    if (!dl?.content_b64) return packBlob;
    const tpl = await JSZip.loadAsync(b64ToU8(dl.content_b64));
    const pack = await JSZip.loadAsync(await packBlob.arrayBuffer());

    // 1. Theme swap — the single highest-impact change
    const themePath = "ppt/theme/theme1.xml";
    const tplTheme = tpl.file(themePath);
    if (tplTheme) {
      const themeXml = await tplTheme.async("string");
      pack.file(themePath, themeXml);
    }

    // 2. Copy any custom fonts embedded in the template
    tpl.folder("ppt/fonts")?.forEach((relPath, file) => {
      if (!file.dir) file.async("uint8array").then(buf => pack.file(`ppt/fonts/${relPath}`, buf));
    });

    // 3. Copy media referenced by the master slide (logos in header/footer)
    const mediaFolder = tpl.folder("ppt/media");
    if (mediaFolder) {
      const promises = [];
      mediaFolder.forEach((relPath, file) => {
        if (!file.dir) {
          promises.push(file.async("uint8array").then(buf => {
            // Only overwrite if pack doesn't already have this filename
            const dest = `ppt/media/${relPath}`;
            if (!pack.file(dest)) pack.file(dest, buf);
          }));
        }
      });
      await Promise.all(promises);
    }

    // 4. Return merged blob
    const out = await pack.generateAsync({ type: "blob", mimeType:
      "application/vnd.openxmlformats-officedocument.presentationml.presentation" });
    return out;
  } catch (e) {
    // Safe fallback — never break the export
    // eslint-disable-next-line no-console
    console.warn("template merge failed, returning uncustomised pack:", e);
    return packBlob;
  }
}


/**
 * DOCX: copy header + footer + numbering from the user's DOCX template so
 * their letterhead / footer / page numbering apply to the generated doc.
 * MVP scope — merges the template's `word/header1.xml` and `word/footer1.xml`.
 */
export async function applyCustomDocxTemplate(docxBlob) {
  try {
    const dl = await api.templateDownload("docx");
    if (!dl?.content_b64) return docxBlob;
    const tpl = await JSZip.loadAsync(b64ToU8(dl.content_b64));
    const doc = await JSZip.loadAsync(await docxBlob.arrayBuffer());
    for (const p of ["word/header1.xml", "word/footer1.xml", "word/theme/theme1.xml"]) {
      const f = tpl.file(p);
      if (f) doc.file(p, await f.async("string"));
    }
    // Copy any media the template references
    tpl.folder("word/media")?.forEach((rel, file) => {
      if (!file.dir) file.async("uint8array").then(buf => {
        if (!doc.file(`word/media/${rel}`)) doc.file(`word/media/${rel}`, buf);
      });
    });
    return await doc.generateAsync({ type: "blob", mimeType:
      "application/vnd.openxmlformats-officedocument.wordprocessingml.document" });
  } catch (e) {
    console.warn("docx template merge failed:", e);
    return docxBlob;
  }
}
